import { useState } from "react";
import { useTranslation } from "react-i18next";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { createLogin } from "services/login/createLogin";
import {
  ReactQueryKeysAccount,
  ReactQueryKeysGlobal,
} from "services/queryKeys";
import { useRbacRoleNames } from "services/rbac/useRbacRoleNames";
import { createServiceAccount } from "services/serviceAccounts/createServiceAccount";
import { getServiceAccounts } from "services/serviceAccounts/getServiceAccounts";
import { createUser } from "services/users/createUser";
import { CreateUserInput } from "services/users/user.types";

import { LoginFormFields } from "pages/configure/Logins/LoginFormModal";
import { CreatedPayload } from "pages/configure/ServiceAccounts/ServiceAccountFormModal/ModalsWithUser/CreateServiceAccount";
import { ServiceAccountFormFields } from "pages/configure/ServiceAccounts/ServiceAccountFormModal/ModalsWithUser/schema";

import { StatusMessageType } from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";
import { queryClient } from "components/queryClient";

const UserModal = lazyWithPreload(() => import("../UserModal/UserModal"));

const SecretContent = lazyWithPreload(
  () =>
    import(
      "pages/configure/ServiceAccounts/ServiceAccountFormModal/ModalsWithUser/CreateServiceAccount"
    )
);

export const useCreateUser = () => {
  const [createdPayload, setCreatedPayload] = useState<CreatedPayload | null>(
    null
  );
  const [user, setCreateUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  const { putStatusMessage } = useStatusMessageQueue();
  const roleNames = useRbacRoleNames();
  usePreloadComponents([UserModal, SecretContent]);

  const openCreateUser = () => {
    setCreateUser(true);
  };

  const closeCreateUser = () => {
    if (isLoading) {
      return;
    }
    setCreateUser(false);
  };

  const confirmCreate = async (
    user: CreateUserInput,
    options?: {
      login?: LoginFormFields;
      serviceAccount?: ServiceAccountFormFields;
    }
  ) => {
    try {
      setIsLoading(true);
      let createdPayload = null;
      if (options?.login) {
        await createLogin(options.login);
      }
      if (options?.serviceAccount) {
        await createServiceAccount({
          name: options.serviceAccount.name,
          description: options.serviceAccount.description,
          networkPolicyName: options.serviceAccount.networkPolicyName,
          isOrganizationAdmin: options.serviceAccount.isAdmin,
        });

        const [serviceAccount] = await getServiceAccounts(
          options.serviceAccount.name
        );

        createdPayload = {
          serviceAccountName: serviceAccount.serviceAccountName,
          serviceAccountId: serviceAccount.serviceAccountId,
        };
      }
      await createUser(user);

      if (options?.login) {
        await queryClient.invalidateQueries({
          queryKey: [ReactQueryKeysGlobal.logins],
        });
      }
      if (options?.serviceAccount) {
        await queryClient.invalidateQueries({
          queryKey: [ReactQueryKeysGlobal.serviceAccounts],
        });
      }
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.users],
      });
      closeCreateUser();
      putStatusMessage({
        message: t("workspace.create_user.success", {
          userName: user.userName,
        }),
        type: StatusMessageType.Success,
      });
      if (createdPayload) {
        setCreatedPayload({ ...createdPayload, createdUser: user.userName });
      }
    } catch (error: any) {
      console.log(error);
      putStatusMessage({
        message: error.message,
        type: StatusMessageType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createUserMarkup = user ? (
    <UserModal
      onClose={closeCreateUser}
      onSubmit={confirmCreate}
      isLoading={isLoading}
      roleNames={roleNames}
      title={t("workspace.create_user.title")}
      primaryButton="workspace.create_user.submit"
    />
  ) : null;

  const createServiceAccountSecretMarkup = createdPayload ? (
    <SecretContent
      createdPayload={createdPayload}
      onClose={() => setCreatedPayload(null)}
    />
  ) : null;

  return {
    createUserMarkup,
    createServiceAccountSecretMarkup,
    openCreateUser,
  };
};
